import React from 'react';
import { ChevronRight } from 'lucide-react';
import feat1Dark from '../assets/feat-assistants-dark.png';
import feat1Light from '../assets/feat-assistants-light.png';
import feat2Dark from '../assets/feat-pdf-dark.png';
import feat2Light from '../assets/feat-pdf-light.png';
import feat3Dark from '../assets/feat-object-dark.png';
import feat3Light from '../assets/feat-object-light.png';
import { useTheme } from '../context/ThemeContext';

const VUZO_URL = 'https://chat.vuzo.com';

const FeatureShowcase = () => {
  const { isDark } = useTheme();

  const features = [
    {
      id: 1,
      title: "Multiple AI Assistants, One Platform",
      description: "Chat with AI Assistants built for specific roles: Writer, Lawyer, Teacher, Designer, and many more. Get expert-level answers, switch assistants seamlessly, and stay productive.",
      buttonText: "Explore the Platform",
      imagePosition: "right",
      imageDark: feat1Dark,
      imageLight: feat1Light,
    },
    {
      id: 2,
      title: "From PDFs to Insights in Seconds",
      description: "Let AI handle the heavy lifting. Instantly summarize, analyze, and extract key insights from any document without scanning pages line by line.",
      buttonText: "Analyze My Document",
      imagePosition: "left",
      imageDark: feat2Dark,
      imageLight: feat2Light,
    },
    {
      id: 3,
      title: "Identify Objects in a Snap",
      description: "Instantly recognize anything, from objects and landmarks to products and scenes. Experience AI vision that sees what you see in real time.",
      buttonText: "Scan & Discover",
      imagePosition: "right",
      imageDark: feat3Dark,
      imageLight: feat3Light,
    },
  ];

  return (
    <section className="w-full">
      <div className="w-full px-4 text-center lg:text-left">
        {features.map((feature) => (
          <div key={feature.id} className="pb-[48px] md:pb-[72px]">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Content */}
              <div className={`md:px-8 order-2 ${feature.imagePosition === 'left' ? 'lg:order-2' : 'lg:order-1'}`}>
                <h3 className={`text-[22px] md:text-[32px] font-semibold leading-tight pb-4 ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                  {feature.title}
                </h3>
                <p className={`text-[15px] md:text-[16px] mb-6 leading-relaxed lg:max-w-[500px] ${isDark ? 'text-gray-300' : 'text-[#64748B]'}`}>
                  {feature.description}
                </p>
                <a
                  href={VUZO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center px-5 py-3 text-[14px] font-semibold rounded-xl transition-all duration-200 ${
                    isDark
                      ? 'border border-[#28F5AB] text-[#28F5AB] hover:bg-[#28F5AB10]'
                      : 'bg-[#177755] text-white hover:bg-[#0f5b3e] shadow-sm'
                  }`}
                >
                  {feature.buttonText}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              {/* Image */}
              <div className={`order-1 ${feature.imagePosition === 'left' ? 'lg:order-1' : 'lg:order-2'}`}>
                <div className={`rounded-2xl overflow-hidden ${
                  isDark
                    ? 'shadow-[0_8px_40px_rgba(0,0,0,0.5)]'
                    : 'shadow-[0_8px_40px_rgba(0,0,0,0.1)] border border-[#e2e8f0]'
                }`}>
                  <img
                    src={isDark ? feature.imageDark : feature.imageLight}
                    alt={`Feature ${feature.id} showcase`}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeatureShowcase;
